import { useEffect, useState, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

interface ScrollWithSpinnerProps {
  children: ReactNode;
  onLoadingFinish?: () => void;
  hideSpinner?: boolean;
  disableSpinner?: boolean;
  loadingDelay?: number;
  loading?: boolean;
}

export default function ScrollWithSpinner({ 
  children, 
  onLoadingFinish, 
  hideSpinner,
  disableSpinner = false,
  loadingDelay = 500
}: ScrollWithSpinnerProps) {
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (disableSpinner) {
      setLoading(false);
      setReady(true);
      onLoadingFinish?.();
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    setLoading(true);
    setReady(false);

    const timeout = setTimeout(() => {
      setLoading(false);
      setReady(true);
      onLoadingFinish?.();
    }, loadingDelay);

    return () => clearTimeout(timeout);
  }, [pathname, disableSpinner, loadingDelay, onLoadingFinish]);

  return (
    <>
      <AnimatePresence mode="wait">
        {loading && !hideSpinner && !disableSpinner && (
          <motion.div
            className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Logo */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {/* Logo buraya eklenebilir */}
            </motion.div>
            
            {/* İnce yükleme çubuğu */}
            <motion.div 
              className="h-0.5 w-64 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded-full overflow-hidden"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 256 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <motion.div
                className="h-full bg-gray-700 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ 
                  delay: 0.3, 
                  duration: loadingDelay / 1000, 
                  ease: "easeInOut" 
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {ready && (
          <motion.div
            key={pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}